/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, Link, breakpoints } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import useLohpCollection from 'bundles/front-page/hooks/useLohpCollection';
import CompactCollectionProductCard from 'bundles/front-page/redesign-modules/compact-collections/CompactCollectionProductCard';
import CompactCollectionsPlaceholder from 'bundles/front-page/redesign-modules/compact-collections/CompactCollectionsPlaceholder';

type Props = {
  headline: string;
  headlineTarget: string;
  collection: {
    id: string;
    collectionModuleId: string;
    presetCollectionId?: string;
    filterName?: string;
    collectionName?: string;
    facets?: Array<Array<string>>;
  };
};

const styles = {
  container: css`
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: calc(var(--cds-spacing-150) / 2 + var(--cds-spacing-100));
    background-color: var(--cds-color-emphasis-primary-background-weak);
    padding: calc(var(--cds-spacing-150) / 2 + var(--cds-spacing-100));
    border-radius: var(--cds-border-radius-100);

    ${breakpoints.down('sm')} {
      padding: calc(var(--cds-spacing-100) + var(--cds-spacing-50));
      gap: var(--cds-spacing-100);
    }
  `,
  collectionHeadline: css`
    color: var(--cds-color-emphasis-neutral-background-xxstrong) !important;
  `,
  flexGrow: css`
    flex-grow: 1;
  `,
};

const CompactCollection = ({ headline, headlineTarget, collection }: Props) => {
  const track = useTracker();

  const handleHeadlineClick = () => {
    track('click_button', {
      button: { name: 'view_all_products', linkURL: headlineTarget, linkName: headline, linkType: 'internal' },
    });
  };

  const isPresetCollection = !!collection.presetCollectionId;

  const { collection: collectionData, loading: isCollectionsLoading } = useLohpCollection({
    limit: 3,
    queryCollectionId: collection.collectionModuleId,
    additionalInput: isPresetCollection
      ? {
          preset_collection_id: collection.presetCollectionId,
        }
      : {
          collection_name: collection.collectionName,
          facets: collection.facets,
        },
  });

  if (!isCollectionsLoading && !collectionData?.entities.length) return null;

  if (isCollectionsLoading) return <CompactCollectionsPlaceholder />;

  return (
    <div css={styles.container}>
      <Link
        href={headlineTarget}
        variant="quiet"
        icon={<ArrowNextIcon size="small" />}
        iconPosition="after"
        css={styles.collectionHeadline}
        onClick={handleHeadlineClick}
        typographyVariant="subtitleMedium"
      >
        {headline}
      </Link>
      <Grid container direction="column" spacing={8} css={styles.flexGrow}>
        {collectionData?.entities.map((entity, index) => (
          <Grid item key={entity.id} css={styles.flexGrow}>
            <CompactCollectionProductCard
              entity={entity}
              entityIndex={index}
              additionalV3TrackingData={{
                carousel: {
                  id: collection.id ?? collectionData?.collectionId ?? '',
                  model: collectionData?.collectionId ?? '',
                  name: headline ?? '',
                  recommender: collectionData?.recommender,
                },
              }}
            />
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default CompactCollection;
